import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { Award, Download, CheckCircle, RefreshCw } from 'lucide-react';

const CertificateGenerator = ({ user, apiEndpoint, authToken }) => {
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch certificate eligibility on component mount
  useEffect(() => {
    fetchCertificateEligibility();
  }, []);

  const fetchCertificateEligibility = async () => {
    setLoading(true);
    setError(null);

    try {
      // Ensure fallback to environment or default URL
      const API_BASE =
        apiEndpoint ||
        process.env.REACT_APP_API_ENDPOINT ||
        'https://a9csjxv6ha.execute-api.us-east-1.amazonaws.com/prod';

   const response = await fetch(
  `${API_BASE}/java-cert-get-exam-history?email=${user.email}`,
  {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken || ''}`,
    },
  }
);


      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📜 Certificate data received:', data);

      if (data.examHistory && Array.isArray(data.examHistory)) {
        // ✅ FILTER OUT FOCUSED EXAMS - Only count certification tests
        // const certificationExams = data.examHistory.filter(attempt => {
        //   // Only include certification type exams
        //   // Exclude: focused, practice sessions, or any non-certification attempts
        //   const examType = attempt.examType || attempt.type || '';
          
        //   // Check if it's a certification test (not focused)
        //   const isCertification = examType === 'certification' || 
        //                          examType === 'full' || 
        //                          examType === 'mixed' ||
        //                          (!examType && attempt.testNumber); // Legacy: has testNumber = certification
          
        //   // Explicitly exclude focused exams
        //   const isFocused = examType === 'focused' || 
        //                    attempt.setName || 
        //                    (attempt.chapter && attempt.chapter.startsWith('Set '));
          
        //   return isCertification && !isFocused;
        // });

        const certificationExams = data.examHistory.filter(attempt => {
  const examType = attempt.examType || attempt.type || '';
  
  // ✅ ONLY certification tests count (not full/mixed)
  const isCertification = examType === 'certification';
  
  return isCertification;
});

        console.log(`📊 Filtered exams: ${certificationExams.length} certification exams (excluded focused exams)`);
        console.log('📋 Exam types breakdown:', {
          total: data.examHistory.length,
          certification: certificationExams.length,
          filtered: data.examHistory.length - certificationExams.length
        });

        // Deduplicate by unique examId/testNumber (only from certification exams)
        const uniqueExams = {};
        certificationExams.forEach((attempt) => {
          const key = attempt.testNumber || attempt.examId || attempt.examName || 'unknown';
          if (!uniqueExams[key] || new Date(attempt.submittedAt) > new Date(uniqueExams[key].submittedAt)) {
            uniqueExams[key] = attempt; // keep latest
          }
        });

        const uniqueList = Object.values(uniqueExams);
        const attendedCount = Math.min(uniqueList.length, 25);
        const passedCount = Math.min(uniqueList.filter((exam) => (exam.percentage || 0) >= 65).length, 25);

        console.log(`✅ Unique certification exams: ${uniqueList.length}`);
        console.log(`✅ Passed (≥65%): ${passedCount}`);
        console.log(`✅ Attended: ${attendedCount}`);

        setEligibility({
          passedCount,
          attendedCount,
          eligibleForPassCertificate: passedCount >= 20,
          eligibleForAttendanceCertificate: attendedCount >= 20,
          progressToPass: {
            current: passedCount,
            required: 25,
            remaining: Math.max(0, 25 - passedCount),
            percentage: ((passedCount / 25) * 100).toFixed(1)
          },
          progressToAttendance: {
            current: attendedCount,
            required: 25,
            remaining: Math.max(0, 25 - attendedCount),
            percentage: ((attendedCount / 25) * 100).toFixed(1)
          }
        });
      } else if (data.certificateEligibility) {
        // fallback (existing server-provided structure)
        setEligibility(data.certificateEligibility);
      } else {
        // default fallback
        setEligibility({
          passedCount: 0,
          attendedCount: 0,
          eligibleForPassCertificate: false,
          eligibleForAttendanceCertificate: false,
          progressToPass: { current: 0, required: 25, remaining: 25, percentage: '0.0' },
          progressToAttendance: { current: 0, required: 25, remaining: 25, percentage: '0.0' },
        });
      }
    } catch (err) {
      console.error('❌ Failed to fetch certificate eligibility:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate Pass Certificate PDF
  const generatePassCertificate = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Background
    doc.setFillColor(240, 250, 240);
    doc.rect(0, 0, 297, 210, 'F');

    // Border
    doc.setLineWidth(3);
    doc.setDrawColor(16, 185, 129);
    doc.rect(10, 10, 277, 190);
    
    doc.setLineWidth(1);
    doc.setDrawColor(5, 150, 105);
    doc.rect(15, 15, 267, 180);

    // Header
    doc.setFontSize(40);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(16, 185, 129);
    doc.text('CERTIFICATE OF ACHIEVEMENT', 148.5, 40, { align: 'center' });

    // Subtitle
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Java SE 8 Programmer Practice Test Certification for OCAJP 8 1Z0-808', 148.5, 52, { align: 'center' });

    // Body text
    doc.setFontSize(14);
    doc.setTextColor(80, 80, 80);
    doc.text('This is to certify that', 148.5, 70, { align: 'center' });

    // Name
    doc.setFontSize(32);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 150, 105);
    doc.text(user.name.toUpperCase(), 148.5, 90, { align: 'center' });

    // Achievement text
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('has successfully passed', 148.5, 105, { align: 'center' });
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(16, 185, 129);
    doc.text(`${eligibility.passedCount} Unique Certification Test${eligibility.passedCount !== 1 ? 's' : ''}`, 148.5, 117, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('with a minimum score of 65% in each exam', 148.5, 127, { align: 'center' });

    // Date and signature section
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    doc.setFontSize(12);
    doc.text(`Issued on: ${currentDate}`, 148.5, 150, { align: 'center' });
    
    // Signature line
    doc.setLineWidth(0.5);
    doc.setDrawColor(150, 150, 150);
    doc.line(120, 170, 177, 170);
    
    doc.setFontSize(10);
    doc.text('Authorized Signature', 148.5, 177, { align: 'center' });
    
    // Footer
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text('JavaLearn.ClusterMicro.com | Java Certification Practice Platform', 148.5, 188, { align: 'center' });
    
    // Certificate ID
    const certificateId = `PASS-${user.email.substring(0, 5).toUpperCase()}-${Date.now()}`;
    doc.text(`Certificate ID: ${certificateId}`, 148.5, 193, { align: 'center' });

    // Save PDF
    doc.save(`Java_Pass_Certificate_${user.name.replace(/\s+/g, '_')}.pdf`);
  };

  // Generate Attendance Certificate PDF
  const generateAttendanceCertificate = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Background
    doc.setFillColor(240, 248, 255);
    doc.rect(0, 0, 297, 210, 'F');

    // Border
    doc.setLineWidth(3);
    doc.setDrawColor(59, 130, 246);
    doc.rect(10, 10, 277, 190);
    
    doc.setLineWidth(1);
    doc.setDrawColor(37, 99, 235);
    doc.rect(15, 15, 267, 180);

    // Header
    doc.setFontSize(40);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(59, 130, 246);
    doc.text('CERTIFICATE OF PARTICIPATION', 148.5, 40, { align: 'center' });

    // Subtitle
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Java SE 8 Programmer Practice Test Certification for OCAJP 8 1Z0-808', 148.5, 52, { align: 'center' });

    // Body text
    doc.setFontSize(14);
    doc.setTextColor(80, 80, 80);
    doc.text('This is to certify that', 148.5, 70, { align: 'center' });

    // Name
    doc.setFontSize(32);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text(user.name.toUpperCase(), 148.5, 90, { align: 'center' });

    // Achievement text
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('has diligently completed', 148.5, 105, { align: 'center' });
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(59, 130, 246);
    doc.text(`${eligibility.attendedCount} Unique Certification Test${eligibility.attendedCount !== 1 ? 's' : ''}`, 148.5, 117, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('attempting all 56 questions in each exam', 148.5, 127, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text('demonstrating commitment to mastering Java programming', 148.5, 137, { align: 'center' });

    // Date and signature section
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    doc.setFontSize(12);
    doc.text(`Issued on: ${currentDate}`, 148.5, 155, { align: 'center' });
    
    // Signature line
    doc.setLineWidth(0.5);
    doc.setDrawColor(150, 150, 150);
    doc.line(120, 170, 177, 170);
    
    doc.setFontSize(10);
    doc.text('Authorized Signature', 148.5, 177, { align: 'center' });
    
    // Footer
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text('JavaLearn.ClusterMicro.com | Java Certification Practice Platform', 148.5, 188, { align: 'center' });
    
    // Certificate ID
    const certificateId = `ATT-${user.email.substring(0, 5).toUpperCase()}-${Date.now()}`;
    doc.text(`Certificate ID: ${certificateId}`, 148.5, 193, { align: 'center' });

    // Save PDF
    doc.save(`Java_Attendance_Certificate_${user.name.replace(/\s+/g, '_')}.pdf`);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw size={48} style={{ color: '#667eea', animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading certificate data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '600px',
        margin: '40px auto',
        padding: '24px',
        backgroundColor: '#fef2f2',
        borderRadius: '12px',
        border: '1px solid #fecaca'
      }}>
        <p style={{ color: '#dc2626', marginBottom: '16px' }}>
          Failed to load certificate data: {error}
        </p>
        <button
          onClick={fetchCertificateEligibility}
          style={{
            padding: '12px 24px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '40px 20px'
    }}>
    <h2 style={{
  fontSize: '32px',
  fontWeight: 'bold',
  marginBottom: '8px',
  color: '#1f2937',  // ✅ Dark gray - highly visible
  textShadow: 'none'  // ✅ Remove shadow
}}>
  📜 Certificates
</h2>
<p style={{
  fontSize: '16px',
  color: '#374151',  // ✅ Dark gray
  marginBottom: '8px'
}}>
  Earn certificates by completing <strong>Certification Tests only</strong>
</p>
<p style={{
  fontSize: '14px',
  color: '#4b5563',  // ✅ Medium gray - clear contrast
  marginBottom: '32px',
  fontStyle: 'italic'
}}>
  Note: Only numbered Certification Tests (1-25) count. Random, Mixed, and Focused exams are for practice only.
</p>
      <p style={{
        fontSize: '14px',
        color: '#e5e7eb',
        marginBottom: '32px',
        fontStyle: 'italic'
      }}>
        Note: Focused exams (Set-based practice) do not count toward certificates
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {/* Pass Certificate Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          border: eligibility.eligibleForPassCertificate ? '2px solid #10b981' : '2px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Award 
              size={32} 
              style={{ 
                color: eligibility.eligibleForPassCertificate ? '#10b981' : '#9ca3af',
                marginRight: '12px' 
              }} 
            />
            <h3 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              margin: 0,
              color: '#1f2937'
            }}>
              Pass Certificate
            </h3>
          </div>

          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '24px',
            lineHeight: '1.6'
          }}>
            Complete 20 or more <strong>certification tests</strong> with a minimum score of 65% to earn this certificate.
          </p>

          <div style={{
            backgroundColor: '#f9fafb',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Progress:</span>
              <div style={{
                marginTop: '8px',
                height: '8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${eligibility.progressToPass.percentage}%`,
                  backgroundColor: eligibility.eligibleForPassCertificate ? '#10b981' : '#667eea',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>
            <p style={{
              fontSize: '20px',
              fontWeight: 'bold',
              margin: '8px 0 0 0',
              color: eligibility.eligibleForPassCertificate ? '#10b981' : '#667eea'
            }}>
              {eligibility.progressToPass.current} / {eligibility.progressToPass.required} Tests Passed
            </p>
          </div>

          {eligibility.eligibleForPassCertificate ? (
            <button
              onClick={generatePassCertificate}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#31a72f',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Download size={20} />
              Download Certificate
            </button>
          ) : (
            <div style={{
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '14px',
              color: '#92400e'
            }}>
              Pass {eligibility.progressToPass.remaining} more certification test{eligibility.progressToPass.remaining !== 1 ? 's' : ''} to unlock
            </div>
          )}
        </div>

        {/* Attendance Certificate Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          border: eligibility.eligibleForAttendanceCertificate ? '2px solid #3b82f6' : '2px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <CheckCircle 
              size={32} 
              style={{ 
                color: eligibility.eligibleForAttendanceCertificate ? '#3b82f6' : '#9ca3af',
                marginRight: '12px' 
              }} 
            />
            <h3 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              margin: 0,
              color: '#1f2937'
            }}>
              Attendance Certificate
            </h3>
          </div>

          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '24px',
            lineHeight: '1.6'
          }}>
            Attempt all 56 questions in 20 or more <strong>certification tests</strong> to earn this certificate.
          </p>

          <div style={{
            backgroundColor: '#f9fafb',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Progress:</span>
              <div style={{
                marginTop: '8px',
                height: '8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${eligibility.progressToAttendance.percentage}%`,
                  backgroundColor: eligibility.eligibleForAttendanceCertificate ? '#3b82f6' : '#667eea',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>
            <p style={{
              fontSize: '20px',
              fontWeight: 'bold',
              margin: '8px 0 0 0',
              color: eligibility.eligibleForAttendanceCertificate ? '#3b82f6' : '#667eea'
            }}>
              {eligibility.progressToAttendance.current} / {eligibility.progressToAttendance.required} Tests Attended
            </p>
          </div>

          {eligibility.eligibleForAttendanceCertificate ? (
            <button
              onClick={generateAttendanceCertificate}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#31a72f',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Download size={20} />
              Download Certificate
            </button>
          ) : (
            <div style={{
              padding: '12px',
              backgroundColor: '#dcfedbff',
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '14px',
              color: '#1e40af'
            }}>
              Complete {eligibility.progressToAttendance.remaining} more certification test{eligibility.progressToAttendance.remaining !== 1 ? 's' : ''} to unlock
            </div>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{
          fontSize: '18px',
          fontWeight: 'bold',
          marginBottom: '16px',
          color: '#1f2937'
        }}>
          Your Certification Statistics
        </h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px'
        }}>
          <div style={{
            padding: '16px',
            backgroundColor: '#f0fdf4',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>
              Certification Tests Passed (≥65%)
            </p>
            <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981', margin: 0 }}>
              {eligibility.passedCount}
            </p>
          </div>
          <div style={{
            padding: '16px',
            backgroundColor: '#eff6ff',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>
              Certification Tests Fully Attended
            </p>
            <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#3b82f6', margin: 0 }}>
              {eligibility.attendedCount}
            </p>
          </div>
        </div>
        <p style={{
          fontSize: '13px',
          color: '#9ca3af',
          marginTop: '16px',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          📚 Focused exams are excluded from certificate calculations
        </p>
      </div>
    </div>
  );
};

export default CertificateGenerator;